/**
 * ffmpeg.wasm を使って画像スライドから MP4 (H.264) を生成する。
 *
 * 実行環境:
 *   - ブラウザ (Web Worker 経由で使う想定)。ただしテスト容易性のため
 *     Worker 依存は上位層に持たせ、ここでは FFmpeg インスタンスを注入可能にする。
 *   - SharedArrayBuffer が無い環境でもシングルスレッド UMD 版で動作。
 *
 * 出力保証:
 *   - MP4 (H.264 yuv420p) — カーセンサー互換
 *   - 総ファイルサイズが 150MB を超える場合、CRF を 28 に上げて再エンコード
 *     (2 パス目)。それでも超える場合はエラー。
 *   - 無音トラック (BGM は著作権 NG のため)。
 */

import type { FFmpeg } from '@ffmpeg/ffmpeg';

import { blobToUint8Array } from './blob-utils';
import { buildDefaultFilterGraph } from './filter-graph';
import {
  type BuildVideoRequest,
  type BuildVideoResult,
  type VideoProgress,
  VIDEO_SPEC,
} from './types';

/**
 * `@ffmpeg/ffmpeg` の FFmpeg インスタンスを DI するための型。
 * 実行時は Web Worker or メインスレッドで `new FFmpeg()` + load() 済みのものを渡す。
 */
export interface FFmpegHandle {
  readonly ffmpeg: FFmpeg;
  readonly loaded: boolean;
}

export interface BuildVideoOptions {
  readonly handle: FFmpegHandle;
  readonly onProgress?: (p: VideoProgress) => void;
}

/**
 * BuildVideoRequest → MP4 Blob (上位エントリ)。
 *
 * @throws Error - ffmpeg 未ロード / エンコード失敗 / 上限サイズ超過 (2 パス後)
 */
export async function buildVideo(
  request: BuildVideoRequest,
  options: BuildVideoOptions,
): Promise<BuildVideoResult> {
  validateRequest(request, options);
  const started = performance.now();
  const ff = options.handle.ffmpeg;
  const report = (p: VideoProgress): void => options.onProgress?.(p);

  const inputNames = await writeInputs(ff, request, report);
  const graph = buildDefaultFilterGraph(
    request.slides.map((s) => ({ durationSec: s.durationSec })),
    request.logo !== undefined,
  );

  const outBytes = await encodeWithFallback(ff, request, graph, report);
  report({ phase: 'reading-output', ratio: 0 });

  await cleanupFiles(ff, inputNames);

  report({ phase: 'done', ratio: 1 });

  return {
    // TS の Uint8Array<ArrayBufferLike> ↔ BlobPart 不整合を as でブリッジ
    blob: new Blob([outBytes as unknown as BlobPart], { type: 'video/mp4' }),
    mimeType: 'video/mp4',
    width: VIDEO_SPEC.WIDTH,
    height: VIDEO_SPEC.HEIGHT,
    durationSec: graph.totalDurationSec,
    fileSizeBytes: outBytes.length,
    elapsedMs: Math.round(performance.now() - started),
  };
}

// ===== buildVideo サブフェーズ (lint 行数/複雑度対策で分割) =================

function validateRequest(request: BuildVideoRequest, options: BuildVideoOptions): void {
  if (!options.handle.loaded) {
    throw new Error('FFmpeg handle must be loaded before buildVideo');
  }
  if (request.slides.length === 0) {
    throw new RangeError('slides must have at least 1 entry');
  }
}

async function writeInputs(
  ff: FFmpeg,
  request: BuildVideoRequest,
  report: (p: VideoProgress) => void,
): Promise<string[]> {
  report({ phase: 'writing-inputs', ratio: 0 });
  const names: string[] = [];
  const total = request.slides.length + (request.logo ? 1 : 0);
  for (let i = 0; i < request.slides.length; i++) {
    const slide = request.slides[i];
    if (!slide) continue;
    const name = deriveInputName(i, slide.blob.type);
    await ff.writeFile(name, await blobToUint8Array(slide.blob));
    names.push(name);
    report({ phase: 'writing-inputs', ratio: (i + 1) / total });
  }
  if (request.logo) {
    const logoName = 'logo.png';
    await ff.writeFile(logoName, await blobToUint8Array(request.logo));
    names.push(logoName);
    report({ phase: 'writing-inputs', ratio: 1 });
  }
  return names;
}

async function encodeWithFallback(
  ff: FFmpeg,
  request: BuildVideoRequest,
  graph: ReturnType<typeof buildDefaultFilterGraph>,
  report: (p: VideoProgress) => void,
): Promise<Uint8Array> {
  const hasLogo = request.logo !== undefined;
  const crf1 = request.crf ?? VIDEO_SPEC.DEFAULT_CRF;
  const out1 = 'out.mp4';
  await runFfmpegEncode(ff, {
    slides: request.slides,
    hasLogo,
    logoName: 'logo.png',
    complexFilter: graph.complexFilter,
    finalLabel: graph.finalLabel,
    crf: crf1,
    outputName: out1,
    totalDurationSec: graph.totalDurationSec,
    onProgress: (r) => report({ phase: 'encoding', ratio: r, message: `encoding (CRF ${crf1})` }),
  });
  let bytes = await readOutput(ff, out1);

  if (bytes.length > VIDEO_SPEC.MAX_FILE_SIZE_BYTES) {
    const crf2 = VIDEO_SPEC.FALLBACK_CRF;
    const out2 = 'out2.mp4';
    report({
      phase: 'encoding',
      ratio: 0,
      message: `first pass ${(bytes.length / 1024 / 1024).toFixed(1)}MB > 150MB, retry CRF ${crf2}`,
    });
    await runFfmpegEncode(ff, {
      slides: request.slides,
      hasLogo,
      logoName: 'logo.png',
      complexFilter: graph.complexFilter,
      finalLabel: graph.finalLabel,
      crf: crf2,
      outputName: out2,
      totalDurationSec: graph.totalDurationSec,
      onProgress: (r) => report({ phase: 'encoding', ratio: r }),
    });
    bytes = await readOutput(ff, out2);
    if (bytes.length > VIDEO_SPEC.MAX_FILE_SIZE_BYTES) {
      throw new Error(
        `Video size exceeds 150MB even at CRF ${crf2} (got ${(bytes.length / 1024 / 1024).toFixed(
          1,
        )}MB). Reduce slide count or duration.`,
      );
    }
  }
  return bytes;
}

async function cleanupFiles(ff: FFmpeg, names: readonly string[]): Promise<void> {
  for (const name of names) {
    await ff.deleteFile(name).catch(() => undefined);
  }
  await ff.deleteFile('out.mp4').catch(() => undefined);
  await ff.deleteFile('out2.mp4').catch(() => undefined);
}

// ===== ffmpeg exec 実行ラッパ ============================================

interface EncodeArgs {
  readonly slides: BuildVideoRequest['slides'];
  readonly hasLogo: boolean;
  readonly logoName: string;
  readonly complexFilter: string;
  readonly finalLabel: string;
  readonly crf: number;
  readonly outputName: string;
  readonly totalDurationSec: number;
  readonly onProgress?: (ratio: number) => void;
}

async function runFfmpegEncode(ff: FFmpeg, args: EncodeArgs): Promise<void> {
  const cliArgs = buildFfmpegArgs(args);
  const handler = (e: { progress?: number; time?: number }): void => {
    if (typeof e.progress === 'number') {
      args.onProgress?.(Math.min(1, Math.max(0, e.progress)));
    } else if (typeof e.time === 'number' && args.totalDurationSec > 0) {
      args.onProgress?.(Math.min(1, e.time / (args.totalDurationSec * 1_000_000)));
    }
  };
  // ffmpeg の stderr (log) を捕捉してエラー時に詳細を含める。
  // Wave 18: 田中さん本番テストで "failed to encode" のみでは原因不明だったため追加。
  const logBuffer: string[] = [];
  const logHandler = (e: { message?: string }): void => {
    if (e.message) logBuffer.push(e.message);
  };
  ff.on('progress', handler);
  ff.on('log', logHandler);
  try {
    const code = await ff.exec(cliArgs);
    if (code !== 0) {
      const tail = logBuffer.slice(-30).join('\n');
      throw new Error(`ffmpeg exited with code ${code}\n--- ffmpeg log (tail 30) ---\n${tail}`);
    }
  } finally {
    ff.off('progress', handler);
    ff.off('log', logHandler);
  }
}

/** `-loop 1 -t X -i fileN` を各スライド分、末尾にロゴ `-i logo.png`、最後に encode オプション。 */
function buildFfmpegArgs(args: EncodeArgs): string[] {
  const cli: string[] = [];
  for (let i = 0; i < args.slides.length; i++) {
    const slide = args.slides[i];
    if (!slide) continue;
    cli.push(
      '-loop',
      '1',
      '-t',
      slide.durationSec.toString(),
      '-i',
      deriveInputName(i, slide.blob.type),
    );
  }
  if (args.hasLogo) {
    cli.push('-i', args.logoName);
  }
  cli.push(
    '-filter_complex',
    args.complexFilter,
    '-map',
    args.finalLabel,
    '-c:v',
    'libx264',
    // Wave 18: preset を medium → ultrafast に変更
    // 80 枚 × 138 秒のエンコードが medium だと数分かかり、ブラウザ内 wasm の
    // メモリ保持が長引いてクラッシュする症状。ultrafast は画質が若干落ちるが
    // 動画スライド用途では十分、エンコード時間 1/5、メモリ使用量も削減。
    '-preset',
    'ultrafast',
    '-crf',
    args.crf.toString(),
    '-pix_fmt',
    'yuv420p',
    '-r',
    VIDEO_SPEC.FPS.toString(),
    '-movflags',
    '+faststart',
    '-an',
    '-y',
    args.outputName,
  );
  return cli;
}

async function readOutput(ff: FFmpeg, name: string): Promise<Uint8Array> {
  const data = await ff.readFile(name);
  if (typeof data === 'string') {
    throw new Error(`ffmpeg readFile returned string (expected binary): ${name}`);
  }
  return data;
}

function deriveInputName(idx: number, mime: string): string {
  const ext = mime === 'image/png' ? 'png' : 'jpg';
  return `in_${idx.toString().padStart(3, '0')}.${ext}`;
}
